import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, Trash2, Cloud, UserPlus, Search, KeyRound, Wand2, Eye, EyeOff, BellRing, RotateCcw, ChevronDown, ChevronRight, AlertTriangle, Music2, Server, Palette } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtBytes } from "@/components/OneDriveQuota";
import { UsageBubble } from "@/components/StoragePill";
import { StorageOverview } from "@/components/StorageOverview";
import { useOnMoveDone } from "@/components/MoveToOneDrive";
import { AdminSecurityActions, ApprovalBanner, SecurityBadges } from "@/components/AdminSecurity";
import { setThemesEnabled } from "@/lib/theme";

const ACCESS = { none: "No access", listen: "Read-only (listen)", download: "Read-only + download" };

const PRESETS = [1, 5, 10];

const LimitInput = ({ u, onUpdate }) => {
  const [v, setV] = useState(u.storage_limit_mb ? String(+(u.storage_limit_mb / 1024).toFixed(2)) : "");
  const apply = (gb) => {
    const mb = Math.round(gb * 1024);
    if (mb !== (u.storage_limit_mb || 0)) onUpdate(u, { storage_limit_mb: mb });
  };
  const save = () => {
    const gb = v.trim() === "" ? 0 : Number(v);
    if (Number.isNaN(gb) || gb < 0) return toast.error("Enter a size in GB, or leave empty for no limit");
    apply(gb);
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-500">
      <input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        placeholder="∞" inputMode="decimal" data-testid={`admin-storage-limit-${u.id}`}
        className="h-7 w-14 rounded border border-zinc-200 px-2 font-mono text-xs outline-none focus:border-zinc-900" />GB
      {PRESETS.map((g) => (
        <button key={g} onClick={() => { setV(String(g)); apply(g); }} data-testid={`admin-storage-preset-${g}-${u.id}`}
          className={`rounded-full border px-2 py-0.5 font-mono text-[10px] transition-colors ${u.storage_limit_mb === g * 1024 ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-900"}`}>{g} GB</button>
      ))}
    </div>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const AlwaysOn = ({ testid }) => <span className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700" data-testid={testid}>Always on</span>;

const SourceRow = ({ u, k, label, testid, onUpdate }) => (
  <label className="flex items-center justify-between gap-3 text-xs text-zinc-700">
    {label}
    {u.role === "owner" ? <AlwaysOn testid={testid} /> : <Switch checked={u[k]} onCheckedChange={(v) => onUpdate(u, { [k]: v })} data-testid={testid} className="scale-75" />}
  </label>
);

const UploadCell = ({ u, onUpdate, allFj }) => {
  const owner = u.role === "owner";
  const on = [u.upload_onedrive && "OneDrive", u.upload_plex && "Plex", u.role !== "owner" && (u.access_filejump || allFj) && "FileJump"].filter(Boolean);
  return (
    <div className="space-y-2 text-[11px] text-zinc-600">
      <label className="flex items-center gap-2">
        {owner ? <AlwaysOn testid={`admin-upload-app-${u.id}`} /> : <Switch checked={u.upload_app} onCheckedChange={(v) => onUpdate(u, { upload_app: v })} data-testid={`admin-upload-app-${u.id}`} className="scale-75" />}Resonance storage
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={`admin-sources-${u.id}`} className="flex items-center gap-1 rounded-full border border-zinc-200 px-2.5 py-1 text-[11px] text-zinc-600 transition-colors hover:border-zinc-900">
            Own sources{on.length ? `: ${on.join(" · ")}` : ": none"}<ChevronDown size={11} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 space-y-3 p-4" data-testid={`admin-sources-popover-${u.id}`}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Their own accounts</p>
          <SourceRow u={u} k="upload_onedrive" label="Own OneDrive" testid={`admin-upload-od-${u.id}`} onUpdate={onUpdate} />
          <SourceRow u={u} k="upload_plex" label="Own Plex server" testid={`admin-upload-plex-${u.id}`} onUpdate={onUpdate} />
          <p className="text-[10px] text-zinc-400">Lets this account connect and import from its own OneDrive / Plex. Your library is never affected.</p>
          {u.role !== "owner" && (
            <>
              <p className="pt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Your FileJump</p>
              {allFj ? <p className="text-xs text-zinc-700" data-testid={`admin-access-filejump-all-${u.id}`}>See your FileJump songs <span className="ml-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">On for all guests</span></p>
                : <SourceRow u={u} k="access_filejump" label="See your FileJump songs" testid={`admin-access-filejump-${u.id}`} onUpdate={onUpdate} />}
              <p className="text-[10px] text-zinc-400">Off by default — FileJump albums stay hidden from this guest's shared-library view until you allow it.</p>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const buf = new Uint32Array(12);
  crypto.getRandomValues(buf);
  return Array.from(buf, (n) => chars[n % chars.length]).join("");
};

const PasswordDialog = ({ u, onClose }) => {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); setShow(false); }, [u]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${u.id}/password`, { password: pw });
      toast.success(`New password set for ${u.email}`, { description: "Share it with them privately." });
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!u} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="admin-password-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-2xl">Set a new password</DialogTitle></DialogHeader>
        <p className="-mt-2 font-mono text-xs text-zinc-500">{u?.email}</p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" data-testid="admin-password-input" className="pr-10 font-mono" />
            <button onClick={() => setShow(!show)} data-testid="admin-password-show" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-900">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>
          </div>
          <button onClick={() => { setPw(genPassword()); setShow(true); }} data-testid="admin-password-generate" className="flex h-10 items-center gap-1.5 rounded-full border border-zinc-300 px-4 text-xs hover:border-zinc-900"><Wand2 size={13} />Generate</button>
        </div>
        <p className="text-xs text-zinc-500">Their current password stops working right away.</p>
        <DialogFooter>
          <button onClick={save} disabled={busy || pw.length < 6} data-testid="admin-password-save" className="flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-2 text-sm text-white disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />}Set password
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Detail = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
    <div className="mt-1 text-xs text-zinc-700">{children}</div>
  </div>
);

const DetailsBody = ({ u, onUpdate, onDelete, onPassword }) => (
    <>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Detail label="Joined" testid={`admin-detail-joined-${u.id}`}>{fmtDate(u.created_at)}</Detail>
        <Detail label="Last seen" testid={`admin-detail-seen-${u.id}`}>{u.last_login_at ? fmtDate(u.last_login_at) : "Never signed in"}</Detail>
        <Detail label="Library" testid={`admin-detail-library-${u.id}`}>
          {u.albums} albums · {u.tracks} songs · {u.playlists} playlists
          {u.onedrive && <p className="mt-0.5 flex items-center gap-1 text-sky-700"><Cloud size={11} />{u.onedrive}</p>}
        </Detail>
        <Detail label="Resonance storage" testid={`admin-detail-storage-${u.id}`}>
          {fmtBytes(u.storage_used || 0)} used{u.role !== "owner" && u.storage_limit_mb ? ` of ${fmtBytes(u.storage_limit_mb * 1048576)}` : ""}
          {u.role !== "owner" && u.upload_app && <div className="mt-2"><LimitInput key={u.storage_limit_mb} u={u} onUpdate={onUpdate} /></div>}
        </Detail>
        <Detail label="Last played" testid={`admin-detail-lastplay-${u.id}`}>
          {u.last_play ? <><span className="font-medium">{u.last_play.title}</span> · {u.last_play.artist}<p className="text-[10px] text-zinc-400">{fmtDate(u.last_play.played_at)}</p></> : "Nothing yet"}
        </Detail>
        <Detail label="Listening (30 days)" testid={`admin-detail-plays-${u.id}`}>{u.plays_30d} play{u.plays_30d === 1 ? "" : "s"}</Detail>
        <Detail label="Favorites" testid={`admin-detail-favs-${u.id}`}>{u.favorites} song{u.favorites === 1 ? "" : "s"}</Detail>
        <Detail label="Security" testid={`admin-detail-security-${u.id}`}><SecurityBadges u={u} /></Detail>
        {u.role !== "owner" && <Detail label="Copied from your library" testid={`admin-detail-copied-${u.id}`}>{u.copied} song{u.copied === 1 ? "" : "s"}</Detail>}
      </div>
      {!u.approved && <ApprovalBanner u={u} onUpdate={onUpdate} />}
      {u.disabled && u.reactivation_requested_at && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md bg-amber-50 px-3 py-2" data-testid={`admin-reactivation-${u.id}`}>
          <p className="flex items-center gap-1 text-[11px] text-amber-800"><BellRing size={11} />Asked to be reactivated · {fmtDate(u.reactivation_requested_at)}</p>
          <button onClick={() => onUpdate(u, { disabled: false })} data-testid={`admin-reactivate-${u.id}`} className="flex h-7 items-center gap-1 rounded-full bg-amber-700 px-3 text-[11px] font-medium text-white hover:bg-amber-800"><RotateCcw size={11} />Reactivate</button>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={() => onPassword(u)} data-testid={`admin-password-${u.id}`} className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 px-3 text-xs transition-colors hover:border-zinc-900"><KeyRound size={13} />Set new password</button>
        <AdminSecurityActions u={u} />
        {!u.is_you && u.role !== "owner" && <button onClick={() => onDelete(u)} data-testid={`admin-delete-${u.id}`} className="flex h-8 items-center gap-1.5 rounded-full border border-red-200 px-3 text-xs text-red-600 transition-colors hover:bg-red-600 hover:text-white"><Trash2 size={13} />Delete account</button>}
      </div>
    </>
);

const UserDetails = (props) => (
  <tr className="border-b border-zinc-100 bg-zinc-50/60 last:border-0" data-testid={`admin-user-details-${props.u.id}`}>
    <td colSpan={5} className="px-5 py-4"><DetailsBody {...props} /></td>
  </tr>
);

const RoleAccess = ({ u, onUpdate }) => (
  <div className="mt-2 flex flex-wrap items-center gap-2">
    {u.is_you ? <span className="font-mono text-[11px] text-zinc-500" data-testid={`admin-role-you-${u.id}`}>You · Owner</span> : (
      <Select value={u.role} onValueChange={(role) => onUpdate(u, { role })}>
        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`admin-role-select-${u.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="owner" data-testid={`admin-role-owner-${u.id}`}>Owner</SelectItem>
          <SelectItem value="guest" data-testid={`admin-role-guest-${u.id}`}>Guest</SelectItem>
        </SelectContent>
      </Select>
    )}
    {u.is_library_owner ? <span className="font-mono text-[11px] text-amber-700" data-testid={`admin-access-owner-${u.id}`}>Library owner</span>
      : u.role === "owner" ? (!u.is_you && <span className="font-mono text-[11px] text-zinc-500">Full library (owner)</span>) : (
      <Select value={u.library_access} onValueChange={(library_access) => onUpdate(u, { library_access })}>
        <SelectTrigger className="h-8 w-44 text-xs" data-testid={`admin-access-select-${u.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(ACCESS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`admin-access-${k}-${u.id}`}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
  </div>
);

const UserCard = ({ u, open, onToggle, onUpdate, onDelete, onPassword, allFj }) => (
  <div className={`rounded-lg border border-zinc-200 bg-white p-4 ${u.disabled && !u.reactivation_requested_at ? "opacity-60" : ""}`} data-testid={`admin-card-${u.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{u.name || "—"}</p>
        <p className="truncate font-mono text-xs text-zinc-500">{u.email}</p>
      </div>
      <label className="flex shrink-0 items-center gap-2 text-[11px] text-zinc-600">
        <Switch checked={!u.disabled} onCheckedChange={(v) => onUpdate(u, { disabled: !v })} disabled={u.is_you} data-testid={`admin-card-active-${u.id}`} />
        {u.disabled ? "Off" : "Active"}
      </label>
    </div>
    <RoleAccess u={u} onUpdate={onUpdate} />
    {u.role !== "owner" && u.upload_app && <UsageBubble used={u.storage_used} limit={u.storage_limit_mb ? u.storage_limit_mb * 1048576 : null} testid={`admin-card-storage-${u.id}`} className="mt-3" />}
    <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
      <UploadCell u={u} onUpdate={onUpdate} allFj={allFj} />
      <p className="text-right text-[11px] text-zinc-500">{u.albums} albums · {u.tracks} songs<br />{u.playlists} playlists</p>
    </div>
    <button onClick={onToggle} data-testid={`admin-card-details-${u.id}`} aria-expanded={open}
      className="mt-3 flex w-full items-center justify-center gap-1 rounded-full border border-zinc-200 py-1.5 text-xs text-zinc-600 hover:border-zinc-900">
      {open ? "Hide details" : "Details"}<ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="mt-4 border-t border-zinc-100 pt-4" data-testid={`admin-card-body-${u.id}`}><DetailsBody u={u} onUpdate={onUpdate} onDelete={onDelete} onPassword={onPassword} /></div>}
  </div>
);

const UserRow = ({ u, open, onToggle, onUpdate, onDelete, onPassword, allFj }) => (
  <>
    <tr className={`border-b border-zinc-100 last:border-0 ${u.disabled && !u.reactivation_requested_at ? "opacity-50" : ""} ${open ? "bg-zinc-50/40" : ""}`} data-testid={`admin-user-${u.id}`}>
      <td className="px-5 py-3 align-top">
        <div className="flex items-start gap-2">
          <button onClick={onToggle} data-testid={`admin-expand-${u.id}`} aria-expanded={open} title={open ? "Hide details" : "Show details"}
            className="mt-0.5 rounded p-0.5 text-zinc-400 transition-colors hover:text-zinc-900"><ChevronRight size={15} className={`transition-transform ${open ? "rotate-90" : ""}`} /></button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{u.name || "—"}</p>
            <p className="truncate font-mono text-xs text-zinc-500">{u.email}</p>
            <RoleAccess u={u} onUpdate={onUpdate} />
            {u.role !== "owner" && u.upload_app && <UsageBubble used={u.storage_used} limit={u.storage_limit_mb ? u.storage_limit_mb * 1048576 : null} testid={`admin-storage-pill-${u.id}`} className="mt-2.5" />}
          </div>
        </div>
      </td>
      <td className="px-3 py-3 align-top"><UploadCell u={u} onUpdate={onUpdate} allFj={allFj} /></td>
      <td className="px-3 py-3 align-top text-xs text-zinc-600" data-testid={`admin-library-${u.id}`}>
        {u.albums} albums · {u.tracks} songs
        <p className="mt-0.5 text-zinc-400">{u.playlists} playlists{u.plays_30d ? <span className="ml-1 inline-flex items-center gap-1" data-testid={`admin-plays-${u.id}`}>· <Music2 size={10} />{u.plays_30d} plays / 30 d</span> : null}</p>
      </td>
      <td className="px-3 py-3 align-top">
        <label className="flex items-center gap-2 text-[11px] text-zinc-600">
          <Switch checked={!u.disabled} onCheckedChange={(v) => onUpdate(u, { disabled: !v })} disabled={u.is_you} data-testid={`admin-active-switch-${u.id}`} />
          {u.disabled ? "Deactivated" : "Active"}
        </label>
        {u.disabled && u.reactivation_requested_at && !open && <button onClick={onToggle} className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-800 underline-offset-2 hover:underline" data-testid={`admin-reactivation-hint-${u.id}`}><BellRing size={10} />Reactivation requested</button>}
        {!u.approved && !open && <button onClick={onToggle} className="mt-1.5 flex items-center gap-1 text-[10px] text-sky-800 underline-offset-2 hover:underline" data-testid={`admin-approval-hint-${u.id}`}><BellRing size={10} />Awaiting approval</button>}
      </td>
      <td className="px-5 py-3 text-right align-top">
        <button onClick={onToggle} data-testid={`admin-details-${u.id}`} className="h-8 rounded-full border border-zinc-200 px-3 text-xs text-zinc-600 transition-colors hover:border-zinc-900 hover:text-zinc-900">{open ? "Hide" : "Details"}</button>
      </td>
    </tr>
    {open && <UserDetails u={u} onUpdate={onUpdate} onDelete={onDelete} onPassword={onPassword} />}
  </>
);

export const AccountsSection = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [pwUser, setPwUser] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  const isOwner = user?.role === "owner";
  const load = useCallback(() => { if (isOwner) api.get("/admin/users").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, [isOwner]);
  useEffect(() => { load(); }, [load]);
  useOnMoveDone(load);
  useEffect(() => { window.addEventListener("admin:refresh", load); return () => window.removeEventListener("admin:refresh", load); }, [load]);
  const flags = (u) => ({
    storage: u.role !== "owner" && u.upload_app && u.storage_limit_mb > 0 && (u.storage_used || 0) / (u.storage_limit_mb * 1048576) >= 0.9,
    reactivation: u.disabled && !!u.reactivation_requested_at,
    never: !u.last_login_at,
    noaccess: u.role === "guest" && !u.disabled && u.library_access === "none",
  });
  const needs = (u) => { const f = flags(u); return f.storage || f.reactivation || f.never || f.noaccess; };
  const sorted = useMemo(() => [...(data?.users || [])].sort((a, b) =>
    (a.disabled - b.disabled) || ((a.role === "owner" ? 0 : 1) - (b.role === "owner" ? 0 : 1)) || (a.name || a.email).localeCompare(b.name || b.email)), [data]);
  const shown = useMemo(() => sorted.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()))
    .filter((u) => filter === "all" ? true : filter === "guests" ? u.role === "guest" && !u.disabled : filter === "owners" ? u.role === "owner" : filter === "deactivated" ? u.disabled : needs(u)), [sorted, q, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const attention = useMemo(() => {
    const us = data?.users || [];
    return { storage: us.filter((u) => flags(u).storage), reactivation: us.filter((u) => flags(u).reactivation), never: us.filter((u) => flags(u).never), noaccess: us.filter((u) => flags(u).noaccess) };
  }, [data]);
  if (!isOwner) return null;
  if (!data) return <Loader2 className="mt-14 animate-spin text-zinc-400" />;

  const patchUser = (id, upd) => setData((d) => ({ ...d, users: d.users.map((x) => (x.id === id ? { ...x, ...upd } : x)) }));
  const update = async (u, upd) => {
    try {
      const { data: res } = await api.patch(`/admin/users/${u.id}`, upd);
      patchUser(u.id, res);
      if (upd.disabled === false && u.reactivation_requested_at) setData((d) => ({ ...d, pending_reactivations: Math.max(0, d.pending_reactivations - 1) }));
      if (upd.approved === true && !u.approved) setData((d) => ({ ...d, pending_approvals: Math.max(0, (d.pending_approvals || 0) - 1) }));
      toast.success(upd.approved ? `${u.email} approved — they can sign in now`
        : upd.role ? `${u.email} is now ${res.role === "owner" ? "an Owner" : "a Guest"}`
        : upd.library_access ? `${u.email}: ${ACCESS[res.library_access]}`
        : upd.disabled !== undefined ? `${u.email} ${res.disabled ? "deactivated" : "reactivated"}` : `Upload settings saved for ${u.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? Their library, playlists, wishlist, radio and OneDrive link are removed from Resonance. Files in their OneDrive are not touched.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setData((d) => ({ ...d, users: d.users.filter((x) => x.id !== u.id) }));
      toast.success(`Deleted ${u.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleFileJumpAll = async (v) => {
    try {
      await api.put("/admin/settings", { filejump_all_guests: v });
      setData((d) => ({ ...d, filejump_all_guests: v }));
      toast.success(v ? "Every guest can now see your FileJump songs" : "FileJump access is back to per-guest switches");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleSignups = async (v) => {
    try {
      await api.put("/admin/settings", { signups_open: v });
      setData((d) => ({ ...d, signups_open: v }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleApproval = async (v) => {
    try {
      await api.put("/admin/settings", { require_approval: v });
      setData((d) => ({ ...d, require_approval: v }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleThemes = async (v) => {
    try {
      await api.put("/admin/settings", { themes_enabled: v });
      setData((d) => ({ ...d, themes_enabled: v }));
      setThemesEnabled(v);
      toast.success(v ? "Background themes are on for everyone" : "Background themes are off — everyone sees the plain look");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const owners = data.users.filter((u) => u.role === "owner").length;

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">{data.users.length} accounts · {owners} owner{owners === 1 ? "" : "s"}</p>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight" data-testid="admin-title">Accounts & permissions</h2>
          <p className="mt-1 text-xs text-zinc-500">Choose who can browse and play your media library. Everyone keeps their own private library too.</p>
        </div>
      </div>
      <section className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-6" data-testid="admin-signups-section">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"><UserPlus size={18} /></div>
          <div>
            <h2 className="font-medium">Allow new sign-ups</h2>
            <p className="text-xs text-zinc-500">New accounts join as <b>Guests</b> and build their own library from their own OneDrive.</p>
          </div>
        </div>
        <Switch checked={data.signups_open} onCheckedChange={toggleSignups} data-testid="admin-signups-switch" />
      </section>
      <section className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-6" data-testid="admin-approval-section">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-50 text-sky-700"><ShieldCheck size={18} /></div>
          <div>
            <h2 className="font-medium">Approve new accounts before they can sign in</h2>
            <p className="text-xs text-zinc-500">{data.require_approval !== false ? "Open sign-ups wait for an owner's approval (invite-link sign-ups are pre-approved)." : "Off — anyone who signs up can use the app straight away."}{data.pending_approvals > 0 && <b className="ml-1 text-sky-800" data-testid="admin-pending-approvals">{data.pending_approvals} waiting</b>}</p>
          </div>
        </div>
        <Switch checked={data.require_approval !== false} onCheckedChange={toggleApproval} data-testid="admin-approval-switch" />
      </section>
      <section className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-6" data-testid="admin-themes-section">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-fuchsia-50 text-fuchsia-700"><Palette size={18} /></div>
          <div>
            <h2 className="font-medium">Background themes</h2>
            <p className="text-xs text-zinc-500">{data.themes_enabled !== false ? "The palette button in the top bar lets everyone pick holiday colours or shuffle a look from their cover art." : "Off — the theme button is hidden and everyone sees the plain background."}</p>
          </div>
        </div>
        <Switch checked={data.themes_enabled !== false} onCheckedChange={toggleThemes} data-testid="admin-themes-switch" />
      </section>
      {user?.permissions?.filejump && (
        <section className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-6" data-testid="admin-filejump-all-section">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-zinc-900 text-amber-400"><Server size={18} /></div>
            <div>
              <h2 className="font-medium">FileJump for all guests</h2>
              <p className="text-xs text-zinc-500">{data.filejump_all_guests ? "Every guest with shared-library access can see and play your FileJump songs." : "Off — grant FileJump per guest via “Own sources” on each account, or switch it on for everyone here."}</p>
            </div>
          </div>
          <Switch checked={!!data.filejump_all_guests} onCheckedChange={toggleFileJumpAll} data-testid="admin-filejump-all-switch" />
        </section>
      )}
      <StorageOverview />
      {Object.values(attention).some((l) => l.length) && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="admin-attention-strip">
          <span className="flex items-center gap-1.5 text-xs font-medium text-amber-900"><AlertTriangle size={13} />Needs attention</span>
          {attention.storage.length > 0 && <button onClick={() => { setFilter("attention"); setQ(""); }} data-testid="admin-attention-storage" className="rounded-full bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50">{attention.storage.length} guest{attention.storage.length === 1 ? "" : "s"} at ≥ 90 % storage</button>}
          {attention.reactivation.length > 0 && <button onClick={() => { setFilter("attention"); setQ(""); }} data-testid="admin-attention-reactivation" className="rounded-full bg-white px-3 py-1 text-xs text-amber-800 hover:bg-amber-100">{attention.reactivation.length} reactivation request{attention.reactivation.length === 1 ? "" : "s"}</button>}
          {attention.noaccess.length > 0 && <button onClick={() => { setFilter("attention"); setQ(""); }} data-testid="admin-attention-noaccess" className="rounded-full bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-100">{attention.noaccess.length} guest{attention.noaccess.length === 1 ? "" : "s"} without shared access</button>}
          {attention.never.length > 0 && <button onClick={() => { setFilter("attention"); setQ(""); }} data-testid="admin-attention-never" className="rounded-full bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-100">{attention.never.length} never signed in</button>}
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-xs leading-relaxed text-zinc-500"><ShieldCheck size={14} className="mr-1.5 inline-block text-amber-600" /><b>Owner</b>: uploads to OneDrive, Plex and Resonance storage, plus this page. <b>Guest</b>: their own uploads and (if you allow it) their own OneDrive/Plex; your library is shared <b>read-only</b> via the “Shared library” column — they can listen, favorite, build personal playlists and save songs offline, never change or upload to it.</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-full border border-zinc-200 bg-white p-1" data-testid="admin-filter-chips">
            {[["all", "All"], ["guests", "Guests"], ["owners", "Owners"], ["deactivated", "Deactivated"], ["attention", "Needs attention"]].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`} aria-pressed={filter === k}
                className={`h-7 rounded-full px-3 text-xs transition-colors ${filter === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}>{label}</button>
            ))}
          </div>
        <label className="flex h-9 items-center gap-2 rounded-full border border-zinc-200 bg-white px-3">
          <Search size={14} className="text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts" data-testid="admin-search-input" className="w-48 bg-transparent text-sm outline-none" />
        </label>
        </div>
      </div>
      <div className="space-y-3 md:hidden" data-testid="admin-user-cards">
        {shown.length === 0 && <p className="rounded-lg border border-zinc-200 bg-white px-5 py-8 text-center text-sm text-zinc-500">No accounts match.</p>}
        {shown.map((u) => <UserCard key={u.id} u={u} allFj={data.filejump_all_guests} open={openId === u.id} onToggle={() => setOpenId(openId === u.id ? null : u.id)} onUpdate={update} onDelete={remove} onPassword={setPwUser} />)}
      </div>
      <div className="hidden overflow-x-auto rounded-lg border border-zinc-200 bg-white md:block">
        <table className="w-full table-fixed text-left" data-testid="admin-users-table">
          <thead className="border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
            <tr><th className="w-[38%] px-5 py-3">Account · Role · Shared library</th><th className="px-3 py-3">Uploads</th><th className="px-3 py-3">Library</th><th className="w-36 px-3 py-3">Active</th><th className="w-28 px-5 py-3" /></tr>
          </thead>
          <tbody>{shown.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-zinc-500" data-testid="admin-users-empty">No accounts match.</td></tr>}{shown.map((u) => <UserRow key={u.id} u={u} allFj={data.filejump_all_guests} open={openId === u.id} onToggle={() => setOpenId(openId === u.id ? null : u.id)} onUpdate={update} onDelete={remove} onPassword={setPwUser} />)}</tbody>
        </table>
      </div>
      <PasswordDialog u={pwUser} onClose={() => setPwUser(null)} />
    </div>
  );
};
